import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './EscrowPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const fmtTZS = (n) => {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (num >= 1_000_000) return `TZS ${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000)     return `TZS ${(num / 1_000).toFixed(0)}K`;
  return `TZS ${num.toLocaleString()}`;
};

const EscrowPage = () => {
  const { t } = useTranslation();
  const STATE_META = {
    HELD:   { label: t('escrow.held'),   color: '#f59e0b', bg: '#fffbeb' },
    FROZEN: { label: t('escrow.frozen'), color: '#ef4444', bg: '#fef2f2' },
  };
  const [data, setData]       = useState({ held: [], held_total: 0, frozen: [], frozen_total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [acting, setActing]   = useState({});
  const [toast, setToast]     = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [freezeModal, setFreezeModal] = useState(null); // {orderId}
  const [reason, setReason]   = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/admin/escrow');
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const release = async (orderId) => {
    setActing((a) => ({ ...a, [orderId]: true }));
    try {
      await apiFetch(`/api/admin/escrow/${orderId}/release`, { method: 'POST' });
      showToast(t('escrow.releasedToast'));
      load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setActing((a) => ({ ...a, [orderId]: false }));
    }
  };

  const freeze = async (orderId, freezeReason) => {
    setActing((a) => ({ ...a, [orderId]: true }));
    try {
      await apiFetch(`/api/admin/escrow/${orderId}/freeze`, {
        method: 'POST',
        body: JSON.stringify({ reason: freezeReason }),
      });
      showToast(t('escrow.frozenToast'));
      load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setActing((a) => ({ ...a, [orderId]: false }));
      setFreezeModal(null);
      setReason('');
    }
  };

  const sweep = async () => {
    setSweeping(true);
    try {
      const res = await apiFetch('/api/admin/escrow/sweep', { method: 'POST' });
      const parts = Object.entries(res)
        .filter(([, v]) => typeof v === 'number')
        .map(([k, v]) => `${v} ${k}`);
      showToast(parts.length ? t('escrow.sweepCompleteWithCounts', { counts: parts.join(', ') }) : t('escrow.sweepComplete'));
      load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSweeping(false);
    }
  };

  const renderTable = (rows, kind) => (
    <div className="esc-table-wrap">
      <table className="esc-table">
        <thead>
          <tr>
            <th>{t('escrow.order')}</th>
            <th>{t('escrow.seller')}</th>
            <th>{t('escrow.amount')}</th>
            <th>{t('escrow.state')}</th>
            <th>{kind === 'held' ? t('escrow.shipped') : t('escrow.reason')}</th>
            <th>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const sm   = STATE_META[e.state] || STATE_META.HELD;
            const busy = acting[e.order_id];
            return (
              <tr key={e.order_id}>
                <td><code className="esc-code">{e.order_id}</code></td>
                <td><code className="esc-code">{(e.seller_id || '').slice(0, 12)}…</code></td>
                <td className="esc-amount">{fmtTZS(e.amount)}</td>
                <td>
                  <span className="esc-chip" style={{ color: sm.color, background: sm.bg }}>
                    {sm.label}
                  </span>
                </td>
                <td className="esc-note-cell">
                  {kind === 'held'
                    ? (e.shipped_at ? new Date(e.shipped_at).toLocaleDateString('en-GB') : '—')
                    : (e.reason || '—')}
                </td>
                <td className="esc-actions">
                  <button
                    className="esc-btn esc-btn--success esc-btn--sm"
                    onClick={() => release(e.order_id)}
                    disabled={busy}
                  >
                    {busy ? '…' : t('escrow.release')}
                  </button>
                  {kind === 'held' && (
                    <button
                      className="esc-btn esc-btn--ghost esc-btn--sm"
                      onClick={() => { setFreezeModal({ orderId: e.order_id }); setReason(''); }}
                      disabled={busy}
                    >
                      {t('escrow.freeze')}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="esc-page">
      {toast && <div className={`esc-toast esc-toast--${toast.type}`}>{toast.msg}</div>}

      {freezeModal && (
        <div className="esc-overlay">
          <div className="esc-modal">
            <h3 className="esc-modal-title">{t('escrow.freezeModalTitle')}</h3>
            <p className="esc-modal-sub">
              {t('escrow.freezeModalBody', { orderId: freezeModal.orderId })}
            </p>
            <textarea
              className="esc-note-input"
              placeholder={t('escrow.reasonOptional')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <div className="esc-modal-actions">
              <button className="esc-btn esc-btn--ghost" onClick={() => setFreezeModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="esc-btn esc-btn--danger"
                onClick={() => freeze(freezeModal.orderId, reason)}
                disabled={acting[freezeModal.orderId]}
              >
                {acting[freezeModal.orderId] ? '…' : t('escrow.freeze')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="esc-header">
        <div>
          <h1 className="dashboard-title">{t('nav.escrow')}</h1>
          <p className="dashboard-subtitle">{t('escrow.subtitle')}</p>
        </div>
        <div className="esc-header-actions">
          <button className="esc-refresh" onClick={sweep} disabled={sweeping}>
            {sweeping ? '…' : `⟳ ${t('escrow.sweepNow')}`}
          </button>
          <button className="esc-refresh" onClick={load} disabled={loading}>
            {loading ? '…' : `↻ ${t('common.refresh')}`}
          </button>
        </div>
      </div>

      {error && (
        <div className="esc-error">{error} <button onClick={load}>{t('common.retry')}</button></div>
      )}

      {!error && (
        <div className="esc-row2">
          <div className="esc-summary-card">
            <div className="esc-summary-icon" style={{ background: '#fffbeb' }}>⏳</div>
            <div>
              <div className="esc-summary-label">{t('escrow.held')}</div>
              <div className="esc-summary-val">{fmtTZS(data.held_total)}</div>
              <div className="esc-summary-sub">{t('escrow.orderCount', { count: data.held.length })}</div>
            </div>
          </div>
          <div className="esc-summary-card">
            <div className="esc-summary-icon" style={{ background: '#fef2f2' }}>⚠️</div>
            <div>
              <div className="esc-summary-label">{t('escrow.frozen')}</div>
              <div className="esc-summary-val">{fmtTZS(data.frozen_total)}</div>
              <div className="esc-summary-sub">{t('escrow.frozenOrderCount', { count: data.frozen.length })}</div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="esc-section">
            <h2 className="esc-section-title">{t('escrow.held')} ({data.held.length})</h2>
            {data.held.length === 0
              ? <div className="esc-empty">{t('escrow.nothingHeld')}</div>
              : renderTable(data.held, 'held')}
          </div>
          <div className="esc-section">
            <h2 className="esc-section-title">{t('escrow.frozen')} ({data.frozen.length})</h2>
            {data.frozen.length === 0
              ? <div className="esc-empty">{t('escrow.nothingFrozen')}</div>
              : renderTable(data.frozen, 'frozen')}
          </div>
        </>
      )}

      {loading && (
        <div className="esc-loading"><div className="mp-spinner" /> {t('common.loading')}</div>
      )}
    </div>
  );
};

export default EscrowPage;
